// src/services/hubAppState.js
// Client for the auth hub's per-app user-data thin layer.
//
// FLEET TEMPLATE copy (from apps/the-unspoken-dialogue) with APP_ID changed
// to 'finding-your-voice', plus a shared once-per-login hydrate cache:
// unlike unspoken's single storageService, this app has three independent
// consumers (AuthContext profile, ProgressContext, ToolStorageService), so
// hydrateAppState() lets them share the one hub read per login.
//
// Why: per-app Firestore stores are invisible to account deletion. The hub
// tree (users/{uid}/apps/finding-your-voice) is deleted by construction
// (auth-hub functions/userCleanup.js), so small per-user state lives there.
//
// Rules of the pattern:
//   • Local-first. The hub is a sync target — reads happen once per login
//     (hydrate), never on the hot path of a page. Saves are debounced,
//     coalesced patches.
//   • The callables run on the caller's HUB session (authHubApp), so the
//     server derives identity itself — no uid travels in the payload.
//   • The server stamps the doc-level updatedAt; clients must not.
//
// Australian English throughout.

import { getFunctions, httpsCallable } from 'firebase/functions';
import { authHubApp, authHubAuth } from './firebase';

const APP_ID = 'finding-your-voice';
const HUB_REGION = 'australia-southeast1';
const SAVE_DEBOUNCE_MS = 800;

let callables = null;
function getCallables() {
  if (!authHubApp) return null;
  if (!callables) {
    const fns = getFunctions(authHubApp, HUB_REGION);
    callables = {
      getAppState: httpsCallable(fns, 'getAppState'),
      saveAppState: httpsCallable(fns, 'saveAppState'),
    };
  }
  return callables;
}

/** Whether hub sync is possible in this build (hub app configured). */
export function hubSyncAvailable() {
  return !!authHubApp;
}

/** Whether a hub session is actually signed in — the callables need one. */
export function hubSignedIn() {
  return !!authHubAuth?.currentUser;
}

/**
 * Convert Firestore Timestamp-like values to ISO strings so patches are
 * clean JSON (legacy docs read from the spoke project carry Timestamps).
 */
export function toPlainClient(value) {
  if (value === null || value === undefined) return null;
  if (typeof value.toDate === 'function') return value.toDate().toISOString();
  if (Array.isArray(value)) return value.map(toPlainClient);
  if (typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = toPlainClient(v);
    return out;
  }
  return value;
}

// ── Merge helpers (newest-wins across devices) ──────────────────────────────

/** Millis from whichever freshness stamp a record carries. */
export function tsMillis(value) {
  if (!value) return 0;
  const stamp =
    value.savedAt || value.updatedAt || value.lastActive ||
    value.lastUpdated || value.createdAt || value.timestamp;
  if (!stamp) return 0;
  if (typeof stamp === 'number') return stamp;
  if (typeof stamp.toMillis === 'function') return stamp.toMillis();
  const t = new Date(stamp).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Newest-wins pick between two records (either may be null). */
export function newerDoc(a, b) {
  if (!a) return b || null;
  if (!b) return a;
  return tsMillis(b) > tsMillis(a) ? b : a;
}

/** Fetch the full thin-layer doc for this app, or null. */
export async function fetchAppState() {
  const c = getCallables();
  if (!c) return null;
  try {
    const res = await c.getAppState({ appId: APP_ID });
    const payload = res?.data;
    return payload?.exists ? payload.data : null;
  } catch (err) {
    console.error('[hubAppState] load failed:', err);
    return null;
  }
}

// ── Shared once-per-login hydrate ───────────────────────────────────────────

let hydratePromise = null;

/**
 * The one hub read per login, shared by every consumer. Call
 * resetHydrateCache() at session boundaries (sign-out) so the next login
 * fetches fresh.
 */
export function hydrateAppState() {
  if (!getCallables()) return Promise.resolve(null);
  if (!hydratePromise) hydratePromise = fetchAppState();
  return hydratePromise;
}

export function resetHydrateCache() {
  hydratePromise = null;
}

// ── Debounced, coalescing patch queue ───────────────────────────────────────

let pendingPatch = null;
let debounceTimer = null;

/** Two-level merge so {toolEntries:{t1}} + {toolEntries:{t2}} coalesce. */
function mergePatches(base, extra) {
  const out = { ...base };
  for (const [key, value] of Object.entries(extra)) {
    const bothMaps =
      value && typeof value === 'object' && !Array.isArray(value) &&
      out[key] && typeof out[key] === 'object' && !Array.isArray(out[key]);
    out[key] = bothMaps ? { ...out[key], ...value } : value;
  }
  return out;
}

/** Queue a partial update; multiple queued patches coalesce into one write. */
export function queuePatch(patch) {
  if (!getCallables()) return;
  pendingPatch = mergePatches(pendingPatch || {}, toPlainClient(patch) || {});
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => { flushPatch(); }, SAVE_DEBOUNCE_MS);
}

/** Flush the queue now (login merge, logout, tab hidden). */
export async function flushPatch() {
  clearTimeout(debounceTimer);
  debounceTimer = null;
  if (!pendingPatch) return;
  const patch = pendingPatch;
  pendingPatch = null;
  const c = getCallables();
  if (!c) return;
  try {
    await c.saveAppState({ appId: APP_ID, patch });
  } catch (err) {
    // Keep the failed patch so the next local action retries it; anything
    // queued while the call was in flight wins over the failed snapshot.
    console.error('[hubAppState] save failed (will retry on next change):', err);
    pendingPatch = mergePatches(patch, pendingPatch || {});
  }
}

/**
 * Drop anything queued but not yet sent. Used by clear-my-data so a stale
 * debounced patch can't re-save state the user just erased.
 */
export function discardPendingPatches() {
  clearTimeout(debounceTimer);
  debounceTimer = null;
  pendingPatch = null;
}

/** Immediate write, no debounce — used for clear-my-data. */
export async function savePatchNow(patch) {
  const c = getCallables();
  if (!c) return false;
  try {
    await c.saveAppState({ appId: APP_ID, patch: toPlainClient(patch) });
    return true;
  } catch (err) {
    console.error('[hubAppState] immediate save failed:', err);
    return false;
  }
}

// Best-effort flush when the tab goes to the background so a debounced
// patch isn't lost to a close.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPatch();
  });
}
